import { reactive, ref } from 'vue';
import { skapi } from '@/main';
import { serviceList, serviceIdList, endOfList } from '@/views/project-list';

import { miscUpdater, SIGNUP_ORIGIN, accountApprovedWithin } from '@/code/misc';
import {
	beginMcpOAuthOnLogin,
	getStoredMcpToken,
	clearStoredMcpToken,
	refreshMcpToken,
} from '@/code/mcp_oauth';

export let user: { [key: string]: any } = reactive({});
export let customer: Promise<Customer> = null;
let lastLoggedInUserId: string | null = null;

type Customer = {
	id: string;
	object: 'customer';
	address: {
		city: string;
		country: string;
		line1: string;
		line2: string;
		postal_code: string;
		state: string;
	};
	balance: number;
	created: number;
	currency: string;
	default_source: any;
	delinquent: boolean;
	description: any;
	discount: any;
	email: string;
	invoice_prefix: string;
	invoice_settings: {
		custom_fields: any;
		default_payment_method: any;
		footer: any;
		rendering_options: any;
	};
	livemode: boolean;
	metadata: {
		[key: string]: any;
	};
	name: string;
	next_invoice_sequence: number;
	phone: any;
	preferred_locales: any[];
	shipping: any;
	tax_exempt: string;
	test_clock: any;
};

export let initUser = (userIncoming: any) => {
	for (let k in user) {
		delete user[k];
	}
	if (userIncoming) {
		for (let k in userIncoming) {
			user[k] = userIncoming[k];
		}
	}
};

const getJwtSub = (jwt?: string): string | null => {
	if (!jwt || typeof jwt !== 'string') return null;
	const parts = jwt.split('.');
	if (parts.length !== 3 || !parts[1]) return null;
	try {
		const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		const padding = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
		const payload = JSON.parse(atob(b64 + padding));
		return typeof payload?.sub === 'string' ? payload.sub : null;
	} catch {
		return null;
	}
};

let newsletterSubscriptionStatus: Promise<boolean> | null = null;
export function getNewsletterSubscriptionStatus() {
	if (newsletterSubscriptionStatus === null) {
		newsletterSubscriptionStatus = skapi
			.getNewsletterSubscription({
				group: 'authorized',
			})
			.then((subs) => {
				return !!subs.length;
			});
	}
	return newsletterSubscriptionStatus;
}

export function setNewsletterSubscriptionStatus(status: boolean) {
	if (status) {
		return skapi
			.subscribeNewsletter({
				group: 'authorized',
			})
			.then(() => {
				newsletterSubscriptionStatus = Promise.resolve(true);
			});
	} else {
		return skapi
			.unsubscribeNewsletter({
				group: 'authorized',
			})
			.then(() => {
				newsletterSubscriptionStatus = Promise.resolve(false);
			});
	}
}

// Ensure the MCP server has a live session file for the logged-in user.
// Returns true when an MCP OAuth round-trip was started (the browser is
// leaving the SPA, so callers must NOT mount / call next()); returns false
// when the existing session is fine, the check is skipped, or MCP is
// unreachable (non-fatal — the caller should proceed).
//
// The check is skipped while another OAuth flow is already in flight (a live
// MCP callback, or an inbound skapi/platform OAuth handshake) so we never
// clobber it. Callers decide WHEN to run this: it is gated to login-required
// routes only — the pre-mount check in main.ts (initial deep links) and the
// router's checkUser guard (in-SPA navigation) — never on public/landing
// pages, and never after the app has already painted.
export async function maybeReauthMcp(
	userIncoming: any,
	redirectAfter?: string,
): Promise<boolean> {
	const currentUserSub =
		typeof userIncoming?.user_id === 'string' ? userIncoming.user_id : null;
	if (!currentUserSub) return false;

	try {
		const params = new URLSearchParams(window.location.search);
		// Inbound platform OAuth: e.g. ChatGPT redirected the user to
		// /login?oauth=platform&redirect_uri=...&state=... to authenticate
		// against skapi. login.vue owns that handshake (returnOAuthToMCP);
		// starting an MCP round-trip here would consume those params and the
		// user would never be returned to the calling platform.
		const inboundPlatformFlow =
			params.get('oauth') === 'platform' &&
			!!params.get('redirect_uri') &&
			!!params.get('state');
		const inflight =
			!!sessionStorage.getItem('mcp_oauth:state') ||
			params.get('oauth') === 'skapi' ||
			inboundPlatformFlow ||
			(!!params.get('code') && !!params.get('state'));
		if (inflight) return false;

		const tok = getStoredMcpToken();
		const now = Date.now();
		const tokenSub = getJwtSub(tok?.access_token);
		const expired =
			!tok ||
			(typeof tok.expires_at === 'number' && tok.expires_at < now + 60_000);
		const mismatchedUser =
			!!tok && !!tokenSub && tokenSub !== currentUserSub;

		if (!expired && !mismatchedUser) return false;

		// Try a SILENT refresh first (no redirect): reconnects a user whose
		// grant aged out but whose server session + refresh_token are still
		// valid (~30d). Mirrors the bunnyquery widget's boot. Only fall back to
		// the disruptive full OAuth round-trip when the silent path can't
		// refresh for THIS user (no refresh_token, or an account switch).
		const refreshed = await refreshMcpToken();
		if (refreshed && getJwtSub(refreshed.access_token) === currentUserSub) {
			return false; // reconnected silently; mount / navigate normally
		}

		clearStoredMcpToken();

		// Where to return the user after the round-trip. Callers that know the
		// intended destination (e.g. an in-SPA navigation target) pass it
		// explicitly; otherwise we return them exactly where they already are.
		let target = redirectAfter;
		if (!target) {
			const pathname = window.location.pathname;
			const here = pathname + window.location.search + window.location.hash;
			// A logged-in user shouldn't be dropped back on a sign-in form.
			const isAuthFormRoute = pathname === '/login' || pathname === '/signup';
			target = isAuthFormRoute ? '/my-projects' : here;
		}

		await beginMcpOAuthOnLogin(target);
		return true; // browser is leaving the SPA
	} catch (err) {
		console.error('MCP OAuth bootstrap failed', err);
		return false; // non-fatal: let the caller mount / navigate
	}
}

// Silently re-establish a stale MCP grant via the OAuth refresh_token flow —
// NO redirect. Mirror of the bunnyquery widget's ensureMcpGrantFresh: the agent
// chat's refreshSession (engine 401 recovery) calls this so an MCP 401 (a
// stale/cleaned server session, which a fresh skapi JWT alone does NOT fix)
// reconnects transparently. Resolves true when the grant is fresh for this user
// (or already was), false otherwise — the redirect-based maybeReauthMcp still
// owns the hard case (no refresh_token / account switch).
export async function ensureMcpGrantFresh(
	userIncoming: any = user,
): Promise<boolean> {
	const currentSub =
		typeof userIncoming?.user_id === 'string' ? userIncoming.user_id : null;
	if (!currentSub) return false;
	const tok = getStoredMcpToken();
	const now = Date.now();
	const tokenSub = getJwtSub(tok?.access_token);
	const expired =
		!tok || (typeof tok.expires_at === 'number' && tok.expires_at < now + 60_000);
	const mismatched = !!tok && !!tokenSub && tokenSub !== currentSub;
	if (!expired && !mismatched) return true; // already fresh for this user
	const refreshed = await refreshMcpToken();
	return !!(refreshed && getJwtSub(refreshed.access_token) === currentSub);
}

// How young an account must be for the first-Google-login branch below to
// stamp misc.signup_origin. The write normally happens seconds after creation;
// the window only needs to absorb a retried first login.
const SIGNUP_ORIGIN_MAX_ACCOUNT_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export let userLoginCallback = async (userIncoming: any) => {
	// console.log('User login callback triggered with user:', userIncoming);
	if (userIncoming) {
		const incomingUserId = typeof userIncoming?.user_id === 'string' ? userIncoming.user_id : null;
		if (incomingUserId && lastLoggedInUserId && incomingUserId !== lastLoggedInUserId) {
			// User switched accounts within the same SPA lifecycle.
			// Clear project caches so route-resolved services cannot be reused
			// from a previous identity.
			while (serviceIdList.value.length > 0) {
				serviceIdList.value.pop();
			}
			for (let k in serviceList) {
				delete serviceList[k];
			}
			endOfList.value = false;
		}
		lastLoggedInUserId = incomingUserId;

		let userIncomingMisc = JSON.parse(userIncoming.misc || '{}');

		getStripeCustomer(userIncoming);

		// auto subscribe google users to newsletter
		if (
			userIncoming?.signup_ticket &&
			userIncoming?.signup_ticket === 'OIDPASS' &&
			!userIncomingMisc.googleUserSubscribed
		) {
			await skapi.subscribeNewsletter({
				group: 'authorized',
			});
			userIncomingMisc.googleUserSubscribed = true;
			const miscPatch: Record<string, any> = { googleUserSubscribed: true };

			// A Google account is created server-side by openIdLogin, so it never
			// passes through signup.vue's misc. This branch runs the first time
			// EITHER site sees the account (www.skapi.com sets
			// googleUserSubscribed on its own first login), so a freshly created
			// account landing here was created through bunnyquery. The age guard
			// keeps old skapi accounts that predate the flag from being tagged.
			if (
				!userIncomingMisc.signup_origin &&
				accountApprovedWithin(userIncoming, SIGNUP_ORIGIN_MAX_ACCOUNT_AGE_MS)
			) {
				userIncomingMisc.signup_origin = SIGNUP_ORIGIN;
				miscPatch.signup_origin = SIGNUP_ORIGIN;
			}
			await miscUpdater(miscPatch);
		}

		// NOTE: the MCP session bootstrap used to live here and ran on every
		// login — including autoLogin restores on public pages — which bounced
		// logged-in visitors through the MCP OAuth round-trip even on the
		// landing page, and only after the app had already painted. It now runs
		// via maybeReauthMcp() gated to login-required routes: pre-mount in
		// main.ts (initial deep links, before first paint) and the router's
		// checkUser guard (in-SPA navigation).
	} else {
		lastLoggedInUserId = null;
		while (serviceIdList.value.length > 0) {
			serviceIdList.value.pop();
		}
		for (let k in serviceList) {
			delete serviceList[k];
		}

		endOfList.value = false;
	}
};

let getStripeCustomer = async (user: any) => {
	let misc;

	if (user?.misc && typeof user.misc === 'string') {
		misc = JSON.parse(user.misc);
	} else {
		misc = {};
	}

	if (misc?.stripe_customer_id) {
		// stripe_customer_id exists

		let customer_id = misc.stripe_customer_id;

		// get customer info, and update customer.value
		customer = skapi.clientSecretRequest({
			clientSecretName: 'stripe_test',
			url: `https://api.stripe.com/v1/customers/${customer_id}`,
			method: 'GET',
			headers: {
				Authorization: 'Bearer $CLIENT_SECRET',
			},
		});
	} else {
		// stripe_customer_id does not exist

		// create customer, save customer id in user misc
		customer = skapi.clientSecretRequest({
			clientSecretName: 'stripe_test',
			url: 'https://api.stripe.com/v1/customers',
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				Authorization: 'Bearer $CLIENT_SECRET',
			},
			data: {
				name: user.name || user.email,
				email: user.email,
			},
		});

		// update customer id in user misc
		customer.then(async (c) => {
			if (c) {
				misc.stripe_customer_id = c.id;
				miscUpdater({
					stripe_customer_id: misc.stripe_customer_id,
				});
			}
		});
	}
};
